import type { LexicalEditor, TextFormatType } from "lexical";
import type { LucideIcon } from "lucide-react";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { FORMAT_TEXT_COMMAND } from "lexical";
import {
  Bold,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  List,
  ListOrdered,
  Text,
  Underline,
} from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select";
import { ToggleGroup, ToggleGroupItem } from "../toggle-group";
import { SHORTCUTS } from "./plugins/shortcuts-plugin";
import { useToolbarStateContext } from "./toolbar-state-provider";
import {
  formatBulletList,
  formatHeading,
  formatNumberedList,
  formatParagraph,
} from "./utils";

export const MarkdownEditorToolbar = () => {
  const { blockType: blockTypeState, hasFormat } = useToolbarStateContext();
  const [editor] = useLexicalComposerContext();

  const selectValue =
    blockTypeState && ["paragraph", "h1", "h2", "h3"].includes(blockTypeState)
      ? blockTypeState
      : "paragraph";

  const textFormat = [
    ...(hasFormat("bold") ? ["bold"] : []),
    ...(hasFormat("italic") ? ["italic"] : []),
    ...(hasFormat("underline") ? ["underline"] : []),
  ];

  return (
    <div className="af-mb-2 af-grid af-grid-flow-col af-items-center af-justify-start af-gap-[10px]">
      <MarkdownEditorFormatSelect value={selectValue} editor={editor} />
      <ToggleGroup
        type="multiple"
        value={textFormat}
        size="xs"
        variant="outline"
        onValueChange={(positiveValues) => {
          const currentFormat = textFormat ?? [];
          const intersection = positiveValues
            .filter((x) => !currentFormat.includes(x))
            .concat(currentFormat.filter((x) => !positiveValues.includes(x)));

          intersection.forEach((value) => {
            editor.dispatchCommand(
              FORMAT_TEXT_COMMAND,
              value as TextFormatType,
            );
          });
        }}
      >
        <ToggleGroupItem value="bold">
          <Bold />
        </ToggleGroupItem>
        <ToggleGroupItem value="italic">
          <Italic />
        </ToggleGroupItem>
        <ToggleGroupItem value="underline">
          <Underline />
        </ToggleGroupItem>
      </ToggleGroup>

      <ToggleGroup
        type="single"
        value={blockTypeState ?? ""}
        size="xs"
        variant="outline"
        onValueChange={(value) => {
          const blockType = value || blockTypeState;
          switch (blockType) {
            case "bullet":
              formatBulletList(editor, blockTypeState);
              break;
            case "number":
              formatNumberedList(editor, blockTypeState);
              break;
            default:
              break;
          }
        }}
      >
        <ToggleGroupItem value="bullet">
          <List />
        </ToggleGroupItem>
        <ToggleGroupItem value="number">
          <ListOrdered />
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
};

interface MarkdownEditorFormatSelectProps {
  editor: LexicalEditor;
  value?: string;
  disabled?: boolean;
}

const MarkdownEditorFormatSelect = ({
  editor,
  value,
  disabled,
}: MarkdownEditorFormatSelectProps) => {
  return (
    <Select disabled={disabled} value={value}>
      <SelectTrigger className="af-h-7 af-min-w-40 af-text-xs">
        <SelectValue className="af-text-xs" />
      </SelectTrigger>
      <SelectContent>
        <MarkdownEditorFormatSelectItem
          value="paragraph"
          label={`Paragraph (${SHORTCUTS.NORMAL})`}
          icon={Text}
          onClick={() => formatParagraph(editor)}
        />

        <MarkdownEditorFormatSelectItem
          value="h1"
          label={`Heading 1 (${SHORTCUTS.HEADING1})`}
          icon={Heading1}
          onClick={() => formatHeading(editor, value, "h1")}
        />
        <MarkdownEditorFormatSelectItem
          value="h2"
          label={`Heading 2 (${SHORTCUTS.HEADING2})`}
          icon={Heading2}
          onClick={() => formatHeading(editor, value, "h2")}
        />
        <MarkdownEditorFormatSelectItem
          value="h3"
          label={`Heading 3 (${SHORTCUTS.HEADING3})`}
          icon={Heading3}
          onClick={() => formatHeading(editor, value, "h3")}
        />
      </SelectContent>
    </Select>
  );
};

interface MarkdownEditorFormatSelectItemProps {
  value: string;
  label: string;
  onClick: () => void;
  icon: LucideIcon;
}

function MarkdownEditorFormatSelectItem({
  value,
  label,
  onClick,
  icon: Icon,
}: MarkdownEditorFormatSelectItemProps) {
  return (
    <SelectItem
      value={value}
      onPointerDown={onClick}
      className="af-flex af-gap-1 af-text-xs"
    >
      <div className="af-flex af-items-center">
        <Icon className="af-mr-2 af-h-4 af-w-4" />
        {label}
      </div>
    </SelectItem>
  );
}
